import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { EmptyState } from "../../components/EmptyState";
import { Field } from "../../components/Field";
import { Skeleton } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { ApiError, apiGet, apiSend, serverBesked } from "../../lib/customFetch";
import { useFeatures } from "../../lib/useFeatures";
import { useMe } from "../../lib/useMe";
import styles from "./KoerselPage.module.css";

interface KoerselRow {
  id: string;
  userId: string;
  userName: string;
  dato: string;
  kilometer: string;
  note: string | null;
  projektId: string | null;
  projektNavn: string | null;
  beloebOere: number | null;
}

interface KoerselOverview {
  fra: string;
  til: string;
  takstOerePerKm: number | null;
  totalKilometer: string;
  totalBeloebOere: number | null;
  rows: KoerselRow[];
}

interface Filter {
  fra: string;
  til: string;
}

interface FormValue {
  id: string | null;
  dato: string;
  kilometer: string;
  note: string;
  projektId: string;
}

function inputDato(dato: Date): string {
  const aar = dato.getFullYear();
  const maaned = String(dato.getMonth() + 1).padStart(2, "0");
  const dag = String(dato.getDate()).padStart(2, "0");
  return `${String(aar)}-${maaned}-${dag}`;
}

function startFilter(): Filter {
  const nu = new Date();
  return {
    fra: inputDato(new Date(nu.getFullYear(), nu.getMonth(), 1)),
    til: inputDato(nu),
  };
}

function tomForm(dato = inputDato(new Date())): FormValue {
  return { id: null, dato, kilometer: "", note: "", projektId: "" };
}

function query(filter: Filter): string {
  return new URLSearchParams({ fra: filter.fra, til: filter.til }).toString();
}

function kroneTekst(oere: number | null): string {
  if (oere === null) return "Ikke beregnet";
  return new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
  }).format(oere / 100);
}

function kilometerTekst(kilometer: string): string {
  return `${new Intl.NumberFormat("da-DK", { maximumFractionDigits: 2 }).format(Number(kilometer))} km`;
}

function fejltekst(error: unknown, handling: string): string {
  if (error instanceof ApiError) {
    return serverBesked(error) ?? `Kunne ikke ${handling}. Prøv igen.`;
  }
  return `Kunne ikke ${handling}. Prøv igen.`;
}

export function KoerselPage() {
  const { me, loading: meLoading } = useMe();
  const {
    features,
    loading: featuresLoading,
    error: featuresError,
    retry: retryFeatures,
  } = useFeatures();
  const koerselAktiv = features.some((feature) => feature.key === "koersel");
  const toast = useToast();
  const canAdminister = me?.rolle === "ejer" || me?.rolle === "administrator";
  const readOnly = me?.rolle === "bogholder";
  const canExport = canAdminister || readOnly;
  const [filter, setFilter] = useState<Filter>(startFilter);
  const [aktivtFilter, setAktivtFilter] = useState<Filter>(startFilter);
  const [data, setData] = useState<KoerselOverview | null>(null);
  const [projekter, setProjekter] = useState<Array<{ id: string; navn: string }>>([]);
  const [form, setForm] = useState<FormValue>(tomForm);
  const [takst, setTakst] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hent = useCallback(async (valg: Filter) => {
    setLoading(true);
    setError(null);
    try {
      const [overview, projectOptions] = await Promise.all([
        apiGet<KoerselOverview>(`/api/koersel?${query(valg)}`),
        apiGet<Array<{ id: string; navn: string }>>("/api/koersel/projekter"),
      ]);
      setData(overview);
      setProjekter(projectOptions);
      setTakst(
        overview.takstOerePerKm === null
          ? ""
          : (overview.takstOerePerKm / 100).toFixed(2),
      );
    } catch (caught) {
      setError(fejltekst(caught, "hente kørslen"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!featuresLoading && featuresError === null && koerselAktiv) {
      void hent(aktivtFilter);
    }
  }, [aktivtFilter, featuresError, featuresLoading, hent, koerselAktiv]);

  const submitFilter = (event: FormEvent) => {
    event.preventDefault();
    if (filter.fra > filter.til) {
      setError("Fra-dato skal ligge før eller på til-dato.");
      return;
    }
    setAktivtFilter({ ...filter });
  };

  const submitKoersel = async (event: FormEvent) => {
    event.preventDefault();
    const input = form.kilometer.trim();
    let normalized: string | null = null;
    if (/^\d+$/.test(input)) {
      normalized = input;
    } else if (/^\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?$/.test(input)) {
      normalized = input.replaceAll(".", "").replace(",", ".");
    } else if (/^\d+(?:[.,]\d{1,2})$/.test(input)) {
      normalized = input.replace(",", ".");
    }
    const kilometer = normalized === null ? Number.NaN : Number(normalized);
    if (!Number.isFinite(kilometer) || kilometer <= 0 || kilometer > 100_000) {
      setError("Angiv kilometer mellem 0,01 og 100.000,00.");
      return;
    }
    setSaving(true);
    setError(null);
    const body = {
      dato: form.dato,
      kilometer,
      note: form.note.trim() === "" ? null : form.note.trim(),
      projektId: form.projektId === "" ? null : form.projektId,
    };
    try {
      if (form.id === null) {
        await apiSend("POST", "/api/koersel", body);
        toast.show("Kørslen er gemt.", "success");
      } else {
        await apiSend("PATCH", `/api/koersel/${form.id}`, body);
        toast.show("Kørslen er rettet.", "success");
      }
      setForm(tomForm(form.dato));
      await hent(aktivtFilter);
    } catch (caught) {
      setError(fejltekst(caught, "gemme kørslen"));
    } finally {
      setSaving(false);
    }
  };

  const slet = async (row: KoerselRow) => {
    if (!window.confirm(`Slet ${kilometerTekst(row.kilometer)} den ${row.dato}?`)) return;
    setSaving(true);
    setError(null);
    try {
      await apiSend("DELETE", `/api/koersel/${row.id}`);
      if (form.id === row.id) setForm(tomForm());
      toast.show("Kørslen er slettet.", "success");
      await hent(aktivtFilter);
    } catch (caught) {
      setError(fejltekst(caught, "slette kørslen"));
    } finally {
      setSaving(false);
    }
  };

  const gemTakst = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = takst.replace(",", ".");
    const value = normalized === "" ? null : Number(normalized);
    if (value !== null && (!Number.isFinite(value) || value < 0 || value > 100)) {
      setError("Taksten skal være mellem 0 og 100 kr. pr. km.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiSend("PATCH", "/api/koersel/indstillinger", {
        takstOerePerKm: value === null ? null : Math.round(value * 100),
      });
      toast.show(value === null ? "Taksten er fjernet." : "Taksten er gemt.", "success");
      await hent(aktivtFilter);
    } catch (caught) {
      setError(fejltekst(caught, "gemme taksten"));
    } finally {
      setSaving(false);
    }
  };

  const csvUrl = useMemo(
    () => `/api/koersel/export.csv?${query(aktivtFilter)}`,
    [aktivtFilter],
  );

  if (featuresLoading) {
    return (
      <div className={styles.root} aria-label="Henter funktioner">
        <Skeleton height="8rem" />
      </div>
    );
  }

  if (featuresError !== null) {
    return (
      <div className={styles.root} role="alert">
        <EmptyState
          title="Kørsel kunne ikke åbnes"
          description="Vi kunne ikke hente virksomhedens funktioner."
          cta={
            <div className={styles.actions}>
              <Button onClick={retryFeatures}>Prøv igen</Button>
              <Link href="/">Gå til Overblik</Link>
            </div>
          }
        />
      </div>
    );
  }

  if (!koerselAktiv) {
    return (
      <div className={styles.root}>
        <EmptyState
          title="Kørsel er slået fra"
          description="Kørselsregistrering er ikke aktiveret for virksomheden."
          cta={<Link href="/">Gå til Overblik</Link>}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <header className={styles.pageHeader}>
        <div>
          <h1>Kørsel</h1>
          {!meLoading && <p>
            {readOnly
              ? "Gennemgå kørselsgrundlaget og hent det til videre behandling."
              : canAdminister
              ? "Registrér ture og hent et ensartet kørselsgrundlag."
              : "Registrér og følg dine ture."}
          </p>}
        </div>
        {!meLoading && canExport && (
          <a className={styles.exportLink} href={csvUrl}>Hent CSV</a>
        )}
      </header>

      {error !== null && <div className={styles.error} role="alert">{error}</div>}

      <div className={styles.topGrid}>
        {!readOnly && <Card title={form.id === null ? "Ny kørsel" : "Ret kørsel"}>
          <form className={styles.form} onSubmit={(event) => void submitKoersel(event)}>
            <Field label="Dato" type="date" required value={form.dato} onChange={(event) => setForm((current) => ({ ...current, dato: event.target.value }))} />
            <Field label="Kilometer" inputMode="decimal" required placeholder="12,5" value={form.kilometer} onChange={(event) => setForm((current) => ({ ...current, kilometer: event.target.value }))} />
            {projekter.length > 0 && (
              <label className={styles.fieldLabel}>
                <span>Projekt (valgfrit)</span>
                <select value={form.projektId} onChange={(event) => setForm((current) => ({ ...current, projektId: event.target.value }))}>
                  <option value="">Uden projekt</option>
                  {projekter.map((projekt) => <option key={projekt.id} value={projekt.id}>{projekt.navn}</option>)}
                </select>
              </label>
            )}
            <label className={styles.fieldLabel}>
              <span>Note (valgfri)</span>
              <textarea maxLength={500} rows={3} value={form.note} onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))} />
            </label>
            <div className={styles.actions}>
              <Button type="submit" disabled={saving}>{form.id === null ? "Gem kørsel" : "Gem ændringer"}</Button>
              {form.id !== null && <Button variant="secondary" onClick={() => setForm(tomForm())}>Annuller</Button>}
            </div>
          </form>
        </Card>}

        {canAdminister && (
          <Card title="Takstvisning">
            <form className={styles.form} onSubmit={(event) => void gemTakst(event)}>
              <Field label="Kr. pr. km" inputMode="decimal" placeholder="Fx 3,81" value={takst} onChange={(event) => setTakst(event.target.value)} hint="Tomt felt fjerner beregningen. Kilometer gemmes uændret." />
              <Button type="submit" disabled={saving}>Gem takst</Button>
            </form>
          </Card>
        )}
      </div>

      <Card title="Registreret kørsel">
        <form className={styles.filters} onSubmit={submitFilter}>
          <Field label="Fra dato" type="date" required value={filter.fra} onChange={(event) => setFilter((current) => ({ ...current, fra: event.target.value }))} />
          <Field label="Til dato" type="date" required value={filter.til} onChange={(event) => setFilter((current) => ({ ...current, til: event.target.value }))} />
          <Button type="submit">Vis periode</Button>
        </form>

        {loading && <div className={styles.loading} aria-label="Henter kørsel"><Skeleton height="5rem" /><Skeleton height="5rem" /></div>}
        {!loading && data !== null && (
          <>
            <dl className={styles.totals}>
              <div><dt>Kilometer</dt><dd>{kilometerTekst(data.totalKilometer)}</dd></div>
              <div><dt>Takst</dt><dd>{data.takstOerePerKm === null ? "Ikke sat" : `${(data.takstOerePerKm / 100).toFixed(2).replace(".", ",")} kr./km`}</dd></div>
              <div><dt>Beløb</dt><dd>{kroneTekst(data.totalBeloebOere)}</dd></div>
            </dl>
            {data.rows.length === 0 ? (
              <EmptyState
                title="Ingen kørsel i perioden"
                description={
                  readOnly
                    ? "Vælg en anden periode for at lede efter registreret kørsel."
                    : "Registrér en tur eller vælg en anden periode."
                }
              />
            ) : (
              <div className={styles.list}>
                {data.rows.map((row) => (
                  <article className={styles.row} key={row.id}>
                    <div className={styles.rowMain}>
                      <strong>{kilometerTekst(row.kilometer)}</strong>
                      <span>{row.dato} · {row.userName}</span>
                      {(row.projektNavn !== null || row.note !== null) && <span>{[row.projektNavn, row.note].filter(Boolean).join(" · ")}</span>}
                    </div>
                    <div className={styles.rowAmount}>{kroneTekst(row.beloebOere)}</div>
                    {!readOnly && <div className={styles.rowActions}>
                      <Button variant="ghost" onClick={() => setForm({ id: row.id, dato: row.dato, kilometer: row.kilometer.replace(".", ","), note: row.note ?? "", projektId: row.projektId ?? "" })}>Ret</Button>
                      <Button variant="ghost" disabled={saving} onClick={() => void slet(row)}>Slet</Button>
                    </div>}
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
